<template>
  <MTimelineItem>
    <div style="column-gap: 2px" class="flex flex-wrap items-center">
      <template v-for="(part, index) in auditParts">
        <FlotoLink
          v-if="part.link"
          :key="part.text"
          target="_blank"
          :to="part.link"
        >
          {{ part.text }}
        </FlotoLink>

        <FlotoUserDrawer
          v-else-if="part.user"
          :key="index"
          class="mx-1"
          :user-id="part.user.id"
          :user="part.user"
          show-avatar
          :sync-data-with-trigger="false"
        />

        <GeolocationDrawer
          v-else-if="part.geolocationAudit"
          :key="index"
          :tooltip-data="part.geolocationAudit.name"
          :all-locations="
            getLocationCoordinates(part.geolocationAudit.extraData)
          "
          :style="{ display: 'inline-flex' }"
        >
          <template v-slot:trigger>
            {{ part.geolocationAudit.name }}
          </template>
        </GeolocationDrawer>
        <ApprovalAssetUpdateDrawer
          v-else-if="part.assetAudit"
          :key="part.assetAudit.id"
          :stage-id="part.assetAudit.id"
        >
          <template v-slot:trigger>
            {{ $tc('asset_update') }}
          </template>
        </ApprovalAssetUpdateDrawer>

        <FlotoTaskDrawer
          v-else-if="part.task"
          :key="index"
          :task-id="part.task.id"
          disabled
        >
          <template v-slot:trigger="{ open }">
            <FlotoAnchorLink :aria-label="part.text" @click="open">
              {{ part.text }}
            </FlotoAnchorLink>
          </template>
        </FlotoTaskDrawer>
        <FlotoAnchorLink
          v-else-if="part.externalLink"
          :key="part.text"
          :href="part.externalLink"
          target="_blank"
        >
          {{ part.text }}
        </FlotoAnchorLink>
        <template v-else-if="part.tooltip">
          <MTooltip :key="part.text">
            <template v-slot:trigger>
              <span> '{{ part.text }}' </span>
            </template>
            {{ part.tooltip }}
          </MTooltip>
        </template>
        <template v-else>
          {{ part.text }}
        </template>
      </template>
      <FlotoDotSeparator class="ml-2" />
      <div class="text-light mx-2">
        <small>{{ createdAt | datetime }} ({{ createdAt | timeago }})</small>
      </div>
      <FlotoDotSeparator v-if="showDetails" class="ml-1" />
      <div v-if="showDetails" class="mx-2">
        <FlotoAnchorLink
          :aria-label="$tc('view_change', 2)"
          @click="toggleExpand"
        >
          <small>{{
            expanded ? $tc('hide_change', 2) : $tc('view_change', 2)
          }}</small>
        </FlotoAnchorLink>
      </div>
      <FlotoDotSeparator v-if="$scopedSlots['action']" class="mr-1" />

      <div v-if="$scopedSlots['action']">
        <slot name="action"></slot>
      </div>
    </div>

    <div v-if="expanded" class="mt-2 p-0">
      <div class="audit-values-inline">
        <div class="audit-values-box audit-value-old">
          <div v-if="showingPlainText()"> {{ displayOldValue }}</div>
          <FlotoRichEditor
            v-else
            v-model="displayOldValue"
            disabled
            :affix-toolbar="false"
            class="rich-text-box"
          />
        </div>
        <div class="text-light p-1">
          <MIcon name="arrow-right" size="lg" />
        </div>
        <div class="audit-values-box audit-value-new">
          <div v-if="showingPlainText()"> {{ displayNewValue }}</div>
          <FlotoRichEditor
            v-else
            v-model="displayNewValue"
            disabled
            :affix-toolbar="false"
            class="rich-text-box"
          />
        </div>
      </div>
    </div>
    <template v-slot:dot>
      <MIcon :name="icon" class="text-neutral-light" />
    </template>
  </MTimelineItem>
</template>

<script>
import GeolocationDrawer from '@src/components/geolocation-drawer.vue'
import Replace from 'lodash/replace'
import ApprovalAssetUpdateDrawer from '@src/components/approval/components/approval-asset-update-drawer.vue'
import { authComputed } from '@state/modules/auth'

export default {
  name: 'Audit',
  components: { GeolocationDrawer, ApprovalAssetUpdateDrawer },
  props: {
    owner: { type: Number, required: true },
    text: { type: String, required: true },
    moduleName: { type: String, required: true },
    parentId: { type: Number, required: true },
    links: {
      type: Array,
      default() {
        return []
      },
    },
    createdAt: { type: Number, required: true },
    icon: { type: String, default: 'circle' },
    oldValue: { type: String, default: '' },
    newValue: { type: String, default: '' },
    fieldType: { type: String, default: '' },
    showDetails: { type: Boolean, default: false },
  },
  data() {
    return {
      showDrawer: false,
      expanded: false,
    }
  },
  computed: {
    ...authComputed,
    auditParts() {
      if ((this.links || []).length) {
        const markedString = this.links.reduce((result, link) => {
          return Replace(` ${result}`, ` ${link.text}`, ` ##${link.text}## `)
        }, this.text)
        const auditParts = markedString
          .split('##')
          .filter(Boolean)
          .map((part) => {
            let obj = { text: part }
            const linkPart = this.links.find(({ text }) => text === part)
            if (linkPart) {
              obj = {
                ...obj,
                ...this.getLinkForModel(
                  linkPart.model,
                  linkPart.id,
                  linkPart.text,
                  linkPart.externalLink,
                  linkPart.extraData,
                  linkPart
                ),
              }
            }
            return obj
          })
        return auditParts
      }
      return [{ text: this.text }]
    },
    displayOldValue() {
      return this.oldValue !== '' ? this.oldValue : this.$tc('none')
    },
    displayNewValue() {
      return this.newValue !== '' ? this.newValue : this.$tc('none')
    },
  },
  methods: {
    getLinkForModel(model, id, text, externalLink, extraData, data) {
      if (this.isPortalLogin) {
        return {}
      }
      if (
        [
          this.$constants.REQUEST,
          this.$constants.PROBLEM,
          this.$constants.CHANGE,
          this.$constants.RELEASE,
        ].indexOf(model) >= 0
      ) {
        return {
          link: this.$modules.getModuleRoute('ticket', 'view', {
            params: { id, ticketType: model },
          }),
        }
      }
      if (
        [
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_SOFTWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
        ].indexOf(model) >= 0
      ) {
        return {
          link: this.$modules.getModuleRoute('asset', 'view', {
            params: { id, assetType: model },
          }),
        }
      }
      if ([this.$constants.CMDB].indexOf(model) >= 0) {
        return {
          link: this.$modules.getModuleRoute('cmdb', 'view', {
            params: { id, ciType: model },
          }),
        }
      }
      if (model === this.$constants.SOFTWARE_LICENSE) {
        return {
          link: this.$modules.getModuleRoute('software-license', 'view', {
            params: { id },
          }),
        }
      }
      if (model === this.$constants.ASSET_MOVEMENT) {
        return {
          link: this.$modules.getModuleRoute(
            'asset-management',
            'movement-view',
            {
              params: { id },
            }
          ),
        }
      }
      if (
        (model === this.$constants.USER_SURVEY ||
          model === this.$constants.USER_SURVEY_SCHEDULE) &&
        externalLink
      ) {
        return {
          tooltip: externalLink,
        }
      }
      if (model === this.$constants.USER_SURVEY) {
        return {
          link: this.$modules.getModuleRoute('user-survey', 'view', {
            params: { id },
          }),
        }
      }
      if (model === this.$constants.USER_SURVEY_SCHEDULE) {
        return {
          link: this.$modules.getModuleRoute(
            'user-survey',
            'edit-schedule-survey',
            {
              params: { id },
            }
          ),
        }
      }
      if (model === this.$constants.CONTRACT) {
        return {
          link: this.$modules.getModuleRoute('contract', 'view', {
            params: { id },
          }),
        }
      }
      if (model === this.$constants.PURCHASE) {
        return {
          link: this.$modules.getModuleRoute('purchase', 'view', {
            params: { id },
          }),
        }
      }
      if (model === this.$constants.PATCH) {
        return {
          link: this.$modules.getModuleRoute('patch', 'view', {
            params: { id },
          }),
        }
      }
      if (model === this.$constants.PATCH_REMOTE_DEPLOYMENT) {
        return {
          link: this.$modules.getModuleRoute('remote-deployment', 'view', {
            params: { id, configType: this.$constants.PATCH_REMOTE_DEPLOYMENT },
          }),
        }
      }
      if (model === this.$constants.PACKAGES_REMOTE_DEPLOYMENT) {
        return {
          link: this.$modules.getModuleRoute('remote-deployment', 'view', {
            params: {
              id,
              configType: this.$constants.PACKAGES_REMOTE_DEPLOYMENT,
            },
          }),
        }
      }
      if (model === this.$constants.REGISTRY_REMOTE_DEPLOYMENT) {
        return {
          link: this.$modules.getModuleRoute('remote-deployment', 'view', {
            params: {
              id,
              configType: this.$constants.REGISTRY_REMOTE_DEPLOYMENT,
            },
          }),
        }
      }
      if (model === this.$constants.USER) {
        return {
          user: {
            id,
            name: text,
          },
        }
      }
      if (model === 'request_integreation') {
        return {
          externalLink: externalLink,
        }
      }
      if (model === this.$constants.TASK) {
        return {
          task: {
            id,
            name: text,
          },
        }
      }
      if (model === this.$constants.PROJECT) {
        return {
          link: this.$modules.getModuleRoute('project', 'view', {
            params: { id },
          }),
        }
      }
      if (model === 'asset_geo_location_history') {
        return {
          geolocationAudit: {
            id,
            name: text,
            extraData: extraData,
          },
        }
      }
      if (model === 'maker_checker') {
        return {
          assetAudit: {
            id,
            name: text,
          },
        }
      }
      if (model === 'workflow_instance') {
        return {
          link: this.$modules.getModuleRoute(
            'advanced-workflow',
            'execution-log-view',
            {
              params: {
                moduleName: this.moduleName,
                id: data.refId,
                logId: data.id,
              },
            }
          ),
        }
      }

      return {}
    },
    getLocationCoordinates(data) {
      const coordinates = [
        {
          latLng: {
            lat: data[0],
            lng: data[1],
          },
        },
      ]
      return coordinates
    },
    toggleExpand() {
      this.expanded = !this.expanded
    },
    showingPlainText() {
      return (
        this.fieldType === 'TextAreaField' || this.fieldType === 'TextField'
      )
    },
  },
}
</script>

<style scoped>
.audit-values-inline {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  align-items: flex-start;
  max-width: 100%;
  text-align: left;
}

.audit-values-box {
  flex: 1 1 0%;
  max-width: fit-content;
  max-height: 16rem;
  padding: 8px;
  overflow: auto;
  font-size: 0.75rem;
  word-break: break-word;
  border-radius: 8px;
}

.audit-value-new {
  background-color: var(--secondary-green-hover);
}

.audit-value-old {
  background-color: var(--secondary-red-hover);
}

.rich-text-box {
  padding: 0% !important;
  margin: 0% !important;
  background-color: transparent !important;
  border: none !important;
}
</style>
